import type { ItemClock } from '../model/v3-itinerary';
import {
  clockToMinutes,
  minutesToClock,
} from '@travel-planner/shared/utils/planner-day-clock';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import css from './planner-v3-itinerary.module.scss';

const pillCss = pickCss(css, css.timePill);

/** "09:30 – 11:30" on a day item; red when it overlaps another. */
export const ItemTimePill = ({
  title,
  clock,
  isOpen,
  onToggle,
}: {
  title: string;
  clock: ItemClock;
  isOpen: boolean;
  /** Absent: the time is shown, not edited. */
  onToggle?: () => void;
}) => {
  const overlap = clock.overlaps.length > 0;
  const label = `${clock.start} – ${clock.end}`;
  const hint = overlap
    ? `Overlaps ${clock.overlaps.join(', ')}`
    : onToggle
      ? 'Change the time'
      : undefined;
  if (!onToggle) {
    return (
      <span className={pillCss({ isOverlap: overlap })} title={hint}>
        <Clock aria-hidden="true" size={11} />
        {label}
      </span>
    );
  }
  return (
    <button
      aria-expanded={isOpen}
      aria-label={`Time of ${title}: ${label}${overlap ? ` · ${hint}` : ''}`}
      className={pillCss({ isOverlap: overlap, isEditable: true })}
      onClick={onToggle}
      title={hint}
      type="button"
    >
      <Clock aria-hidden="true" size={11} />
      {label}
    </button>
  );
};

/** Start and end of a day item, as XT's planner reschedules one. */
export const ItemTimeEditor = ({
  title,
  clock,
  onSave,
  onClose,
}: {
  title: string;
  clock: ItemClock;
  onSave: (startTime: string, endTime: string) => void;
  onClose: () => void;
}) => {
  const [start, setStart] = useState(clock.start);
  const [end, setEnd] = useState(clock.end);
  const valid =
    /^\d{2}:\d{2}$/.test(start) &&
    /^\d{2}:\d{2}$/.test(end) &&
    clockToMinutes(end) > clockToMinutes(start);
  // A new start keeps the item's length, as XT's reschedule does.
  const changeStart = (next: string) => {
    if (/^\d{2}:\d{2}$/.test(next) && /^\d{2}:\d{2}$/.test(start)) {
      const length = clockToMinutes(end) - clockToMinutes(start);
      const moved = clockToMinutes(next) + Math.max(length, 0);
      if (length > 0 && moved < 24 * 60) {
        setEnd(minutesToClock(moved));
      }
    }
    setStart(next);
  };
  return (
    <form
      aria-label={`Time of ${title}`}
      className={css.timeEditor}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onSave(start, end);
        onClose();
      }}
    >
      <label className={css.timeField}>
        Start
        <input
          className={css.timeInput}
          onChange={(event) => changeStart(event.target.value)}
          required
          step={300}
          type="time"
          value={start}
        />
      </label>
      <label className={css.timeField}>
        End
        <input
          className={css.timeInput}
          onChange={(event) => setEnd(event.target.value)}
          required
          step={300}
          type="time"
          value={end}
        />
      </label>
      {valid ? null : (
        <span className={css.timeError} role="alert">
          End must be after start
        </span>
      )}
      <span className={css.commentActions}>
        <button className={css.link} onClick={onClose} type="button">
          Cancel
        </button>
        <button className={css.commentSave} disabled={!valid} type="submit">
          Save
        </button>
      </span>
    </form>
  );
};
